import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Calendar, MapPin, Users, CreditCard, Building, AlertCircle, Loader2 } from 'lucide-react';
import { formatDateUK, formatCurrency } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { booking, system, isEquipmentBooking } = location.state || {};
  const [isProcessing, setIsProcessing] = useState(false);

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Booking Not Found</h2>
          <Button onClick={() => navigate(isEquipmentBooking ? '/equipment' : '/browse')} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  const bookingReference = booking.booking_id || booking.id;
  const itemName = isEquipmentBooking ? booking.equipment_name : (system?.name || booking.system_name);
  const itemCategory = isEquipmentBooking ? 'Equipment' : (system?.category || 'PA System');
  const itemImage = isEquipmentBooking 
    ? (booking.equipment_image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80')
    : (system?.image || 'https://images.unsplash.com/photo-1501612780327-45045538702b?w=400&q=80');
  
  // Get the total amount - check multiple possible locations
  const totalAmount = booking.total_amount || booking.base_amount || system?.price || 0;

  const handlePayWithPayFast = async () => {
    setIsProcessing(true);
    
    try {
      const response = await axios.post(`${API}/payments/initiate`, {
        booking_id: bookingReference,
        booking_type: isEquipmentBooking ? 'equipment' : 'pa_system'
      }, { withCredentials: true });

      if (response.data.payment_url) {
        // Redirect to PayFast
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      alert(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Payment Options */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Complete Your Payment</CardTitle>
              <CardDescription className="text-gray-400">
                Choose your preferred payment method
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* PayFast Option */}
              <div className="bg-gray-700 rounded-lg p-5">
                <div className="flex items-center mb-4">
                  <CreditCard className="h-6 w-6 text-green-500 mr-2" />
                  <h3 className="font-semibold text-lg text-white">Pay with PayFast</h3>
                  <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Recommended</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Secure online payment via card, instant EFT, or mobile payment methods.
                </p>
                <Button
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
                  onClick={handlePayWithPayFast}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Redirecting to PayFast...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Pay Now - {formatCurrency(totalAmount)}
                    </>
                  )}
                </Button>
              </div>

              {/* Bank Transfer Option (Alternative) */}
              <div className="bg-gray-700/50 rounded-lg p-5 border border-gray-600">
                <div className="flex items-center mb-4">
                  <Building className="h-6 w-6 text-yellow-500 mr-2" />
                  <h3 className="font-semibold text-lg text-white">Bank Transfer</h3>
                  <span className="ml-2 px-2 py-1 bg-gray-600 text-gray-300 text-xs rounded-full">Alternative</span>
                </div>
                <p className="text-gray-400 text-sm mb-3">
                  Manual bank transfer. Booking will be confirmed once payment is verified (1-2 business days).
                </p>
                <div className="space-y-2 text-sm bg-gray-800 rounded p-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Bank:</span>
                    <span className="text-white">Capitec Bank</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Account:</span>
                    <span className="text-white">1534684946</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Reference:</span>
                    <span className="text-yellow-500 font-mono">{bookingReference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount:</span>
                    <span className="text-white font-bold">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Send proof of payment to: events@byalani.co.za
                </p>
              </div>

              {/* Cancellation Notice */}
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-orange-500 mr-2 mt-0.5" />
                  <div className="text-sm text-orange-200">
                    <p className="font-medium">Cancellation Policy</p>
                    <p className="text-orange-300/80">A 20% cancellation fee applies to all paid bookings.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Booking Summary */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Booking Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <img
                  src={itemImage}
                  alt={itemName}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h3 className="font-bold text-xl mb-1 text-white">{itemName}</h3>
                <p className="text-gray-400">{itemCategory}</p>
                {isEquipmentBooking && booking.quantity > 1 && (
                  <p className="text-sm text-yellow-500 font-medium mt-1">
                    Quantity: {booking.quantity} units
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-3 text-white">Event Details</h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <Calendar className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div className="text-sm">
                        <p className="font-medium text-white">{booking.event_name || booking.eventName}</p>
                        <p className="text-gray-400">{booking.event_type || booking.eventType}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Calendar className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div className="text-sm">
                        <p className="text-gray-500">Date &amp; Time</p>
                        <p className="font-medium text-white">{formatDateUK(booking.event_date)} at {booking.event_time}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div className="text-sm">
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium text-white">{booking.location}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Users className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                      <div className="text-sm">
                        <p className="text-gray-500">Expected Attendees</p>
                        <p className="font-medium text-white">{booking.attendees} people</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <h4 className="font-semibold mb-3 text-white">Price Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">
                        {isEquipmentBooking ? 'Equipment Rental' : 'PA System Rental'}
                      </span>
                      <span className="font-medium text-white">{formatCurrency(totalAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Duration</span>
                      <span className="font-medium text-white">{booking.duration}</span>
                    </div>
                    {isEquipmentBooking && booking.quantity > 1 && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Quantity</span>
                        <span className="font-medium text-white">{booking.quantity} units</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-3 border-t border-gray-700">
                      <span className="font-bold text-white text-lg">Total Due</span>
                      <span className="font-bold text-yellow-500 text-2xl">{formatCurrency(totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking Reference */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-xs text-yellow-400">Booking Reference</p>
                <p className="text-lg font-mono font-bold text-yellow-500">{bookingReference}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Payment;
